import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';
import { DateRangePicker } from '@/sections/attacks/capabilities/components/DateRangePicker';
import { ScheduleWarning } from '@/sections/attacks/capabilities/components/ScheduleWarning';
import {
  WeeklySchedule,
  WeeklyScheduleState,
} from '@/sections/attacks/capabilities/components/WeeklySchedule';
import {
  ScheduleValidationErrors,
  validateScheduleConfig,
} from '@/sections/attacks/capabilities/utils/schedule-validation';

interface EditScheduleModalProps {
  schedule: CapabilitySchedule | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    scheduleId: string,
    updates: {
      weeklySchedule: WeeklyScheduleState;
      startDate: string;
      endDate?: string;
    }
  ) => Promise<void>;
}

/**
 * EditScheduleModal - Modal for editing an existing schedule
 *
 * Allows users to modify the weekly schedule configuration and
 * date range for an existing capability schedule.
 */
export function EditScheduleModal({
  schedule,
  isOpen,
  onClose,
  onSave,
}: EditScheduleModalProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [scheduleState, setScheduleState] =
    useState<WeeklyScheduleState | null>(null);
  const [errors, setErrors] = useState<ScheduleValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when schedule changes
  useEffect(() => {
    if (schedule && isOpen) {
      setScheduleState(schedule.weeklySchedule);
      setStartDate(schedule.startDate.split('T')[0]);
      setEndDate(schedule.endDate?.split('T')[0] || '');
      setErrors({});
    }
  }, [schedule, isOpen]);

  const handleSave = async () => {
    if (!schedule || !scheduleState) {
      return;
    }

    const result = validateScheduleConfig(
      startDate,
      endDate || undefined,
      scheduleState
    );

    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setIsSaving(true);
    try {
      await onSave(schedule.scheduleId, {
        weeklySchedule: scheduleState,
        startDate,
        endDate: endDate || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to update schedule:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!schedule || !scheduleState) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Edit Schedule"
      size="xl"
      footer={{
        text: isSaving ? 'Saving...' : 'Save Changes',
        onClick: handleSave,
        disabled: isSaving,
        styleType: 'primary',
      }}
    >
      <div className="flex flex-col gap-6">
        <ScheduleWarning />

        <WeeklySchedule
          schedule={scheduleState}
          onScheduleChange={setScheduleState}
          error={errors.schedule}
        />

        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          error={errors.dateRange}
        />
      </div>
    </Modal>
  );
}
